import useOnboardReqWithTempToken from "lib/hooks/register/useOnboardReqWithTempToken";
import useUploadImage from "lib/hooks/register/useUploadImage";
import router from "next/router";
import React, { useEffect, useRef, useState } from "react";
import { Cropper } from "react-cropper";
import AlertConfirm from "../atomic/Alert";
import Button from "../atomic/Button";
import { UploadImage } from "../Icon";

interface UploadAvatarProps {
  setAvatar?: (val: any) => void;

  showDefaultAvatar?: boolean; // this check if we need to show default avatar
  reload?: boolean; // reload if we need to refetch the image
}

function UploadAvatar({
  setAvatar,
  showDefaultAvatar,
  reload = false,
}: UploadAvatarProps) {
  // Upload Ref for the upload button and alertRef for Crop Modal
  const uploadRef = useRef<HTMLInputElement>(null);
  const alertRef = useRef<HTMLDivElement>(null);
  const { uploadImage, success } = useUploadImage();
  // Getting Viewport of the window
  const [viewPort, setViewPort] = useState<{ width: number; height: number }>(
    () => ({
      width: typeof window === "undefined" ? 0 : window.innerWidth,
      height: typeof window === "undefined" ? 0 : window.innerHeight,
    }),
  );

  // States related to the cropper and image

  const [zoomLevel, setZoomLevel] = useState(1);
  const [image, setImage] = useState("");
  const [cropper, setCropper] = useState<any>();
  const [file, setFile] = useState<File>();

  useEffect(() => {
    const getViewPort = () => {
      setViewPort({
        width: document.documentElement.clientWidth,
        height: document.documentElement.clientHeight,
      });
    };
    window.addEventListener("resize", getViewPort);
    return () => {
      window.removeEventListener("resize", getViewPort);
    };
  }, []);

  // style for the cropper according to viewport
  const style = {
    width:
      viewPort.width > 620
        ? "500px"
        : viewPort?.width > 400
          ? "330px"
          : "280px",
    height:
      viewPort?.width > 620
        ? "500px"
        : viewPort?.width > 400
          ? "330px"
          : "280px",
  };

  // this reference to the input element for upload file as button is used
  const handleFileUpload = () => {
    uploadRef.current && uploadRef.current.click();
  };

  // this handles the change of the file in input element
  const handleFileChange = (e: any) => {
    e.preventDefault();

    let files;

    if (e.dataTransfer) {
      files = e.dataTransfer.files;
    } else if (e.target) {
      files = e.target.files;
    }

    setFile(files[0]);

    const reader = new FileReader();

    reader?.readAsDataURL(files && files[0]);
    alertRef.current && alertRef.current.click();
    reader.onload = () => {
      setImage(reader.result as any);
    };
  };

  // gets the cropped image data and set to avatar
  const getCropData = () => {
    if (typeof cropper !== "undefined") {
      cropper.getCroppedCanvas().toBlob((blob: any) => {
        file && uploadImage(blob, `${file.name.split(".")[0]}.png`, reload);
      });

      setAvatar && setAvatar(cropper.getCroppedCanvas().toDataURL());
    }
  };

  return (
    <>
      <AlertConfirm
        showCancel={false}
        ref={alertRef}
        heading="Crop image and upload"
        onConfirm={() => {
          getCropData();
          setImage("");
        }}
        confirmText="Confirm and upload"
      >
        {" "}
        <div className="w-full">
          <Cropper
            style={style}
            initialAspectRatio={1 / 1}
            src={image}
            viewMode={3}
            dragMode="move"
            aspectRatio={1 / 1}
            minCropBoxHeight={10}
            minCropBoxWidth={10}
            background={false}
            responsive={true}
            zoomTo={zoomLevel / 10}
            onInitialized={(instance) => {
              setCropper(instance);
            }}
            guides={true}
          />
        </div>
        <div className="flex items-center">
          -&nbsp;
          <input
            type="range"
            className="w-full"
            min={1}
            max={100}
            value={zoomLevel}
            onChange={(e) => setZoomLevel(Number(e.target.value))}
          />
          &nbsp;+
        </div>
      </AlertConfirm>

      <input
        ref={uploadRef}
        className="opacity-0 h-0 w-0"
        type="file"
        name="image"
        accept="image/png, image/jpeg"
        id="image"
        onChange={handleFileChange}
        size={Number(process.env.NEXT_PUBLIC_IMAGESIZE_LIMIT) || 20971520}
      />

      <Button
        onClick={handleFileUpload}
        className="flex justify-center items-center gap-2 font-medium rounded-lg px-5 py-2.5 mt-2 text-sm border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
      >
        <UploadImage className="inline-block w-4 h-4" />
        Upload Image
      </Button>
    </>
  );
}

export default UploadAvatar;
